import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useAuth } from './contexts/AuthContext';
import { AgentSessionProvider } from './contexts/AgentSessionContext';
import { Navbar } from './components/layout/Navbar';
import { ChatWindow } from './components/chat/ChatWindow';
import { Loader2, TrendingUp } from 'lucide-react';
import './styles/index.css';

const LoginView = lazy(() => import('./components/auth/LoginView').then((m) => ({ default: m.LoginView })));
const PendingApproval = lazy(() => import('./components/auth/PendingApproval').then((m) => ({ default: m.PendingApproval })));
const StateInspector = lazy(() => import('./components/portfolio/StateInspector').then((m) => ({ default: m.StateInspector })));
const ChatHistoryDrawer = lazy(() => import('./components/chat/ChatHistoryDrawer').then((m) => ({ default: m.ChatHistoryDrawer })));

const AppContent: React.FC = () => {
  const { currentUser, userProfile, loading } = useAuth();
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (!historyOpen && !inspectorOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setHistoryOpen(false);
        setInspectorOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [historyOpen, inspectorOpen]);

  // 1. Loading Screen
  if (loading) {
    return (
      <div className="full-screen-loader">
        <div className="loader-content">
          <div className="loader-logo-wrapper">
            <TrendingUp size={36} className="text-accent" />
          </div>
          <Loader2 size={32} className="spin text-accent" />
          <p>Connecting to Firebase & Cloud Firestore...</p>
        </div>
      </div>
    );
  }

  // 2. Unauthenticated: Show Login View
  if (!currentUser) {
    return (
      <Suspense
        fallback={
          <div className="full-screen-loader">
            <Loader2 size={32} className="spin text-accent" />
          </div>
        }
      >
        <LoginView />
      </Suspense>
    );
  }

  // 3. Authenticated but user_status != 'APPROVED' (e.g. 'NEW'): Show Pending Approval Screen
  if (userProfile?.user_status !== 'APPROVED') {
    return (
      <Suspense
        fallback={
          <div className="full-screen-loader">
            <Loader2 size={32} className="spin text-accent" />
          </div>
        }
      >
        <PendingApproval />
      </Suspense>
    );
  }

  const handleToggleHistory = () => {
    if (!historyOpen && typeof window !== 'undefined' && window.innerWidth < 1024) {
      setInspectorOpen(false);
    }
    setHistoryOpen(!historyOpen);
  };

  const handleToggleInspector = () => {
    if (!inspectorOpen && typeof window !== 'undefined' && window.innerWidth < 1024) {
      setHistoryOpen(false);
    }
    setInspectorOpen(!inspectorOpen);
  };

  // 4. Authenticated & user_status == 'APPROVED': Full Access to Chat & Multi-Agent Workspace
  return (
    <AgentSessionProvider>
      <div className="app-shell">
        <Navbar
          onToggleInspector={handleToggleInspector}
          inspectorOpen={inspectorOpen}
          onToggleHistory={handleToggleHistory}
          historyOpen={historyOpen}
        />
        <main className="main-content-row">
          {(historyOpen || inspectorOpen) && (
            <div
              className="drawer-mobile-backdrop"
              onClick={() => {
                setHistoryOpen(false);
                setInspectorOpen(false);
              }}
              aria-label="Close active drawer"
            />
          )}
          <Suspense fallback={null}>
            {historyOpen && (
              <ChatHistoryDrawer
                isOpen={historyOpen}
                onClose={() => setHistoryOpen(false)}
              />
            )}
          </Suspense>
          <div className="chat-primary-pane">
            <ChatWindow />
          </div>
          <Suspense fallback={null}>
            {inspectorOpen && (
              <StateInspector onClose={() => setInspectorOpen(false)} />
            )}
          </Suspense>
        </main>
      </div>
    </AgentSessionProvider>
  );
};

export const App: React.FC = () => {
  return <AppContent />;
};

export default App;
